import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    Input,
    Select,
    Text,
    Textarea,
    VStack,
    HStack,
    useToast,
    FormErrorMessage,
    Alert,
    AlertIcon,
    Spinner
} from '@chakra-ui/react';
import {
    FiTarget,
    FiPlus,
    FiArrowLeft,
    FiEye,
    FiSave
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { CohortCondition, LIST_OPERATORS } from './CohortCondition';
import { buildTraitConditionPayload, isTraitConditionComplete } from './traitCondition';
import API from '../../../helpers/axiosInstance';

const CohortForm = ({
    onBack,
    onSave,
    editingCohort = null,
    isLoading = false
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        refreshFrequency: 'daily'
    });
    // Cohort-level combinator: 'all' = contact must meet ALL conditions (AND),
    // 'any' = contact meets ANY condition (OR).
    const [match, setMatch] = useState('all');
    const navigate = useNavigate();
    const [conditions, setConditions] = useState([
        {
            id: 1,
            type: 'event',
            event: '',
            property: '',
            operator: 'equals',
            value: '',
            time_frame: 'all_time'
        }
    ]);

    const [events, setEvents] = useState([]);
    const [eventAttributes, setEventAttributes] = useState({});
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingAttributes, setLoadingAttributes] = useState({});
    const [errors, setErrors] = useState({});
    const [estimatedUsers, setEstimatedUsers] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const toast = useToast();
    const [estimatingUsers, setEstimatingUsers] = useState(false);
    const param = useParams()

    // API Headers
    const getApiHeaders = () => ({
        "Content-Type": "application/json",
        "x-api-key": localStorage.getItem('api-key'),
        "x-access-token": localStorage.getItem('customer-portal'),
    });

    // Fetch unique events on component mount
    useEffect(() => {
        fetchUniqueEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize form data when editingCohort changes
    useEffect(() => {
        if (editingCohort && !isInitialized) {


            setFormData({
                name: editingCohort.name || '',
                description: editingCohort.description || '',
                refreshFrequency: editingCohort.refresh_frequency || 'daily',
            });
            setMatch(editingCohort.match === 'any' ? 'any' : 'all');

            // ✅ If editing, populate conditions
            if (editingCohort.conditions && editingCohort.conditions.length > 0) {
                const mappedConditions = editingCohort.conditions.map((cond, index) => {

                    let formattedValue = cond.value;
                    if (Array.isArray(cond.value)) {
                        formattedValue = cond.value.join(',');
                    }

                    return {
                        ...cond,
                        id: index + 1,
                        type: cond.type || 'event',
                        event: cond.event || '',
                        property: cond.property || '',
                        operator: cond.operator || 'equals',
                        value: formattedValue || '',
                        time_frame: cond.time_frame || 'all_time',
                        custom_date_range: cond.custom_date_range
                            ? {
                                start_date: cond.custom_date_range.start_date,
                                end_date: cond.custom_date_range.end_date,
                            }
                            : undefined,
                    };
                });


                setConditions(mappedConditions);

                // Fetch attributes for existing events
                const uniqueEvents = [...new Set(mappedConditions.map(c => c.event).filter(Boolean))];
                uniqueEvents.forEach(eventName => {
                    fetchEventAttributes(eventName, `init_${eventName}`);
                });
            }

            setIsInitialized(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingCohort, isInitialized]);

    // Reset initialization flag when editingCohort changes
    useEffect(() => {
        setIsInitialized(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingCohort?.id || editingCohort?._id]);

    const fetchUniqueEvents = async () => {
        setLoadingEvents(true);
        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
                {},
                { headers: getApiHeaders() }
            );

            if (response.data.status) {
                setEvents(response.data.data.events);

            } else {
                toast({
                    title: "Error",
                    description: response.data.message || "Failed to fetch events",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch events";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchEventAttributes = async (eventName, conditionId) => {
        if (!eventName) return;

        // Check if we already have attributes for this event
        if (eventAttributes[eventName]) {
            return;
        }

        setLoadingAttributes(prev => ({ ...prev, [conditionId]: true }));

        try {
            const response = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`,
                { event: eventName },
                { headers: getApiHeaders() }
            );

            if (response.data.status) {
                setEventAttributes(prev => ({
                    ...prev,
                    [eventName]: response.data.data.attributes
                }));

            } else {
                toast({
                    title: "Error",
                    description: response.data.message || "Failed to fetch event attributes",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching event attributes:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch event attributes";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingAttributes(prev => ({ ...prev, [conditionId]: false }));
        }
    };

    const handleEventChange = (eventName, conditionId) => {
        if (eventName && !eventAttributes[eventName]) {
            fetchEventAttributes(eventName, conditionId);
        }
    };

    const mapConditionForApi = (condition) => {
        switch (condition.type) {
            case 'event':
                return {
                    type: condition.type,
                    event: condition.event,
                    property: condition.property,
                    operator: condition.operator,
                    value: LIST_OPERATORS.includes(condition.operator)
                        ? (Array.isArray(condition.value)
                            ? condition.value
                            : String(condition.value || '').split(',').map(v => v.trim()).filter(Boolean))
                        : condition.value,
                    time_frame: condition.time_frame,
                    // Exclusion: customer did NOT perform this event → contact NOT IN (...)
                    ...(condition.did_not_perform ? { did_not_perform: true } : {}),
                    ...(condition.time_frame === 'custom_range' && condition.custom_date_range
                        ? { custom_date_range: condition.custom_date_range }
                        : {}),
                };
            case 'tag':
                return { type: condition.type, tag: condition.tag };
            case 'device_type':
                return { type: condition.type, device_type: condition.device_type };
            case 'customer_profile':
                return {
                    type: condition.type,
                    profile_key: condition.profile_key,
                    operator: condition.operator,
                    value: condition.value,
                };
            case 'trait':
                /**
                 * FEAT-ATTRIB-003 `C2`. Built by the same module the condition
                 * editor validates with, so the shape the author saw accepted
                 * is the shape that is sent — see `traitCondition.js`.
                 */
                return buildTraitConditionPayload(condition);
            case 'other_contacts':
                return { type: condition.type, contact_type: condition.contact_type, condition: condition.condition };
            case 'interactions': {
                const interactionBase = { type: condition.type, touchpoint: condition.touchpoint, interaction_type: condition.interaction_type };
                if (condition.interaction_type === 'visited_more_than') {
                    return { ...interactionBase, interactionCount: condition.interactionCount, interactionDays: condition.interactionDays };
                }
                if (condition.interaction_type === 'not_visited_in') {
                    return { ...interactionBase, interactionDays: condition.interactionDays };
                }
                if (condition.interaction_type === 'visited_and_used') {
                    return { ...interactionBase, value: condition.value };
                }
                return interactionBase;
            }
            default:
                return { type: condition.type };
        }
    };

    // Type-safe value presence check (ANTI-17, DA-BUG-0214): cohort condition
    // `value` may be a string OR a number (numeric operators persist as Number);
    // plain `?.trim()` would throw TypeError on numbers. Coerce to string before
    // trimming and treat any non-empty value (including 0) as present. Guard
    // explicitly by type.
    const hasValue = (v) => {
        if (v === undefined || v === null) return false;
        if (typeof v === 'number') return Number.isFinite(v);
        return String(v).trim() !== '';
    };

    const isConditionComplete = (c) => {
        switch (c.type) {
            case 'event':
                // "Did NOT perform" needs only the event; property/operator/value
                // are an optional filter (and only complete-checked if a property is set).
                if (c.did_not_perform) {
                    return c.event && (!c.property || (c.operator && hasValue(c.value)));
                }
                return c.event && c.property && c.operator && hasValue(c.value);
            case 'tag':              return !!c.tag;
            case 'device_type':      return !!c.device_type;
            case 'customer_profile': return c.profile_key && c.operator && hasValue(c.value);
            case 'trait':            return isTraitConditionComplete(c);
            case 'other_contacts':   return c.contact_type && c.condition;
            case 'interactions': {
                if (!c.touchpoint || !c.interaction_type) return false;
                if (c.interaction_type === 'visited_more_than') return c.interactionCount && c.interactionDays;
                if (c.interaction_type === 'not_visited_in') return !!c.interactionDays;
                if (c.interaction_type === 'visited_and_used') return !!c.value;
                return true;
            }
            default:                 return false;
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Segment name is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        const invalidConditions = conditions.filter(c => !isConditionComplete(c));
        if (invalidConditions.length > 0) {
            newErrors.conditions = 'All conditions must be fully filled in';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const addCondition = () => {
        const newCondition = {
            id: Math.max(...conditions.map(c => c.id)) + 1,
            type: 'event',
            event: '',
            property: '',
            operator: 'equals',
            value: '',
            time_frame: 'all_time',
            did_not_perform: false
        };
        setConditions([...conditions, newCondition]);
    };

    const updateCondition = (updatedCondition) => {
        setConditions(conditions.map(c =>
            c.id === updatedCondition.id ? updatedCondition : c
        ));
        // Clear condition errors when updating
        if (errors.conditions) {
            setErrors({ ...errors, conditions: null });
        }
    };

    const deleteCondition = (conditionId) => {
        setConditions(conditions.filter(c => c.id !== conditionId));
    };

    const estimateUsers = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please complete the form before estimating users",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setEstimatingUsers(true);

        try {
            const apiConditions = conditions.map(mapConditionForApi);




            const response = await API.post(
                `${process.env.REACT_APP_COHORT}/cohorts/estimate-users`,
                { conditions: apiConditions, match },
                { headers: getApiHeaders() }
            );



            if (response.data.status) {
                const estimatedCount = response.data.data.estimated_users;
                setEstimatedUsers(estimatedCount);

                toast({
                    title: "Estimation Complete",
                    description: `Estimated ${estimatedCount.toLocaleString()} users match this segment`,
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Estimation Error",
                    description: response.data.message || "Failed to estimate users",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error estimating users:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to estimate users";

            toast({
                title: "Estimation Error",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
            });

            // Clear previous estimation on error
            setEstimatedUsers(null);
        } finally {
            setEstimatingUsers(false);
        }
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast({
                title: "Validation Error",
                description: "Please fix the errors before saving",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsSaving(true);

        const apiConditions = conditions.map(mapConditionForApi);


        const cohortData = {
            name: formData.name,
            description: formData.description,
            refresh_frequency: formData.refreshFrequency,
            match,
            conditions: apiConditions,
            created_by: JSON.parse(localStorage.getItem("customer"))?.username
        };



        try {
            // Determine if creating or updating
            const isEditing = editingCohort && (editingCohort._id || editingCohort.id);
            const url = isEditing
                ? `${process.env.REACT_APP_COHORT}/cohorts/${editingCohort._id || editingCohort.id}`
                : `${process.env.REACT_APP_COHORT}/cohorts/create`;

            const method = isEditing ? 'PUT' : 'POST'; // Using PUT for updates

            const response = await API({
                method: method,
                url: url,
                data: cohortData,
                headers: getApiHeaders()
            });



            if (response.data.status) {
                toast({
                    title: "Success",
                    description: response.data.message || `Segment ${isEditing ? 'updated' : 'created'} successfully`,
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });

                // Call onSave callback with the response data
                if (onSave) {
                    onSave(response.data.data || cohortData);
                }
            } else {
                toast({
                    title: "Error",
                    description: response.data.message || `Failed to ${isEditing ? 'update' : 'create'} segment`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error saving cohort:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to save segments";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const isFormIncomplete = () => {
        if (!formData.name.trim() || !formData.description.trim()) return true;
        return conditions.some(c => !isConditionComplete(c));
    };

    const handlePreview = () => {
        navigate(`/cohorts/detail/${param?.id}`)
    }
    return (
        <Card className='card-style' style={{ margin: "20px 40px" }} shadow={'xl'}>

            <Box p={6} maxW="100%" >
                <Button
                    leftIcon={<FiArrowLeft />}
                    variant="outline"
                    onClick={handleBack}
                    size={"sm"}
                    _hover={{ bg: "#0000001a !important", color: "black !important" }}
                    aria-label="Back to segments"
                    mb={4}
                >Back To Segments</Button>
                <Flex justifyContent={"space-between"}>
                    <HStack mb={6}>

                        <VStack align="start" spacing={0}>
                            <Heading size="md">
                                {editingCohort ? 'Edit Segment' : 'Create New Segment'}
                            </Heading>
                            <Text color="gray.600">Define user segments using events, features, and properties</Text>
                        </VStack>


                    </HStack>
                    <Flex>

                        {editingCohort && (
                            <Button
                                leftIcon={<FiEye />}
                                variant="outline"
                                size={'sm'}
                                isDisabled={loadingEvents || isSaving}
                                me={3}
                                onClick={handlePreview}
                            >
                                Preview
                            </Button>
                        )}
                        {estimatedUsers ? (
                            <Button variant={"outline"} size={"sm"} me={3}>~ {estimatedUsers?.toLocaleString()} users</Button>
                        ) : ""}
                        <Button
                            leftIcon={<FiSave />}
                            colorScheme="blue"
                            size="sm"
                            onClick={handleSave}
                            isLoading={isSaving}
                            isDisabled={
                                loadingEvents ||
                                Object.values(loadingAttributes).some(loading => loading) ||
                                isFormIncomplete()
                            }
                            loadingText={editingCohort ? 'Updating...' : 'Creating...'}
                            className='updated-chakra-button'
                        >
                            {editingCohort ? 'Update Segment' : 'Save Segment'}
                        </Button>

                    </Flex>
                </Flex>

                <Grid templateColumns="2fr 1fr" gap={8}>
                    <GridItem>
                        <VStack spacing={6} align="stretch">
                            {/* Basic Information */}
                            <Card>
                                <CardHeader>
                                    <Heading size="md">Basic Information</Heading>
                                    <Text color="gray.600" fontSize="sm">
                                        Define the name, description, and refresh settings for your segment
                                    </Text>
                                </CardHeader>
                                <CardBody>
                                    <VStack spacing={4} align="stretch">
                                        <FormControl isRequired isInvalid={errors.name}>
                                            <FormLabel>Segment Name</FormLabel>
                                            <Input
                                                placeholder="e.g., High-Value Customers"
                                                value={formData.name}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, name: e.target.value });
                                                    if (errors.name) setErrors({ ...errors, name: null });
                                                }}
                                                bg="white"
                                            />
                                            <FormErrorMessage>{errors.name}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isRequired isInvalid={errors.description}>
                                            <FormLabel>Description</FormLabel>
                                            <Textarea
                                                placeholder="Describe what this segment represents and how it will be used..."
                                                value={formData.description}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, description: e.target.value });
                                                    if (errors.description) setErrors({ ...errors, description: null });
                                                }}
                                                rows={3}
                                                bg="white"
                                            />
                                            <FormErrorMessage>{errors.description}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Refresh Frequency</FormLabel>
                                            <Select
                                                value={formData.refreshFrequency}
                                                onChange={(e) => setFormData({ ...formData, refreshFrequency: e.target.value })}
                                                bg="white"
                                            >
                                                <option value="hourly">Hourly</option>
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                            </Select>
                                        </FormControl>
                                    </VStack>
                                </CardBody>
                            </Card>

                            {/* Cohort Conditions */}
                            <Card>
                                <CardHeader>
                                    <Flex justify="space-between" align="center">
                                        <VStack align="start" spacing={0}>
                                            <Heading size="md">Segment Conditions</Heading>
                                            <Text color="gray.600" fontSize="sm">
                                                Define the criteria that users must meet to be included in this segment
                                            </Text>
                                        </VStack>
                                        <HStack>
                                            {conditions.length > 1 && (
                                                <FormControl w="auto">
                                                    <FormLabel fontSize="xs" mb={1} color="gray.600">Customer must match</FormLabel>
                                                    <Select
                                                        size="sm"
                                                        value={match}
                                                        onChange={(e) => setMatch(e.target.value)}
                                                        bg="white"
                                                    >
                                                        <option value="all">ALL conditions (AND)</option>
                                                        <option value="any">ANY condition (OR)</option>
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </HStack>
                                    </Flex>
                                </CardHeader>
                                <CardBody>
                                    {errors.conditions && (
                                        <Alert status="error" mb={4}>
                                            <AlertIcon />
                                            {errors.conditions}
                                        </Alert>
                                    )}

                                    {conditions.map((condition, index) => (
                                        <CohortCondition
                                            key={condition.id}
                                            condition={condition}
                                            conditionIndex={index}
                                            onUpdate={updateCondition}
                                            onDelete={deleteCondition}
                                            canDelete={conditions.length > 1}
                                            events={events}
                                            eventAttributes={eventAttributes}
                                            onEventChange={handleEventChange}
                                            isLoadingAttributes={loadingAttributes[condition.id] || false}
                                        />
                                    ))}
                                    <Flex justifyContent={"center"} alignItems={"center"}>
                                        <Button
                                            leftIcon={<FiPlus />}
                                            w={"md"}
                                            variant="outline"
                                            size="sm"
                                            onClick={addCondition}
                                        >
                                            Add Another Condition
                                        </Button>
                                    </Flex>
                                </CardBody>
                            </Card>
                        </VStack>
                    </GridItem>

                    <GridItem>
                        <VStack spacing={6} align="stretch">
                            {/* Quick Actions */}
                            <Card>
                                <CardHeader>
                                    <Heading size="md">Quick Actions</Heading>
                                </CardHeader>
                                <CardBody>
                                    <VStack spacing={3}>
                                        <Button
                                            leftIcon={<FiTarget />}
                                            variant="outline"
                                            w="full"
                                            onClick={estimateUsers}
                                            isDisabled={loadingEvents || isSaving || estimatingUsers}
                                            isLoading={estimatingUsers}
                                            loadingText="Estimating..."
                                        >
                                            Estimate Users
                                        </Button>


                                        {estimatedUsers && (
                                            <Box
                                                p={3}
                                                bg="blue.50"
                                                borderRadius="md"
                                                w="full"
                                                textAlign="center"
                                                border="1px"
                                                borderColor="blue.200"
                                            >
                                                <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                                    Estimated Users
                                                </Text>
                                                <Text fontSize="lg" fontWeight="bold" color="blue.800">

                                                    {estimatedUsers?.toLocaleString()}
                                                </Text>
                                            </Box>
                                        )}
                                    </VStack>
                                </CardBody>
                            </Card>

                            {/* Tips */}
                            <Card>
                                <CardHeader>
                                    <Heading size="md">Tips</Heading>
                                </CardHeader>
                                <CardBody>
                                    <VStack align="start" spacing={3}>
                                        <Text fontSize="sm">• Select an event first to load available properties</Text>
                                        <Text fontSize="sm">• Properties are dynamically loaded based on your selected event</Text>
                                        <Text fontSize="sm">• Use multiple conditions to create more specific segments</Text>
                                        <Text fontSize="sm">• Time frames help narrow down when events occurred</Text>
                                        <Text fontSize="sm">• String operators work with text values, numeric operators with numbers</Text>
                                    </VStack>
                                </CardBody>
                            </Card>

                            {/* Validation */}
                            <Card>
                                <CardHeader>
                                    <Heading size="md">Validation</Heading>
                                </CardHeader>
                                <CardBody>
                                    {loadingEvents ? (
                                        <HStack>
                                            <Spinner size="xs" />
                                            <Text fontSize="sm" color="blue.500">Loading events...</Text>
                                        </HStack>
                                    ) : Object.keys(errors).length > 0 || isFormIncomplete() ? (
                                        <Text fontSize="sm" color="red.500">
                                            ⚠️ Complete required fields
                                        </Text>
                                    ) : (
                                        <Text fontSize="sm" color="green.500">
                                            ✅ Ready to use
                                        </Text>
                                    )}
                                </CardBody>
                            </Card>



                        </VStack>
                    </GridItem>
                </Grid>
            </Box>
        </Card>
    );
};

export default CohortForm;